import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Users,
  CheckCircle,
  XCircle,
  SkipForward,
  ArrowRight,
  GitMerge,
  HelpCircle,
} from 'lucide-react';
import { api, type AdjudicationItem, type AdjudicationStats } from '../services/api';

export default function AdjudicationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const queryClient = useQueryClient();

  // Handle selected item from URL params
  useEffect(() => {
    const selected = searchParams.get('selected');
    if (selected) {
      setSelectedItem(selected);
      // Clear the URL param after reading it
      setSearchParams({});
      // Show all statuses to find the item
      setStatusFilter('');
    }
  }, [searchParams, setSearchParams]);

  // Fetch adjudication queue
  const { data: queueData, isLoading } = useQuery({
    queryKey: ['adjudication-queue', statusFilter],
    queryFn: () => api.getAdjudicationQueue({
      status: statusFilter || undefined,
      limit: 50,
    }),
  });

  // Fetch stats
  const { data: stats } = useQuery<AdjudicationStats>({
    queryKey: ['adjudication-stats'],
    queryFn: () => api.getAdjudicationStats(),
  });

  // Fetch selected item details
  const { data: itemDetail } = useQuery({
    queryKey: ['adjudication-item', selectedItem],
    queryFn: () => api.getAdjudicationItem(selectedItem!),
    enabled: !!selectedItem,
  });

  // Fetch why analysis for selected item
  const { data: whyData } = useQuery({
    queryKey: ['why-entities', itemDetail?.item?.entityId1, itemDetail?.item?.entityId2],
    queryFn: () => api.getWhyEntities(itemDetail!.item.entityId1, itemDetail!.item.entityId2),
    enabled: !!itemDetail?.item?.entityId1 && !!itemDetail?.item?.entityId2,
  });

  // Mutations
  const approveMutation = useMutation({
    mutationFn: (id: string) => api.approveAdjudication(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adjudication-queue'] });
      queryClient.invalidateQueries({ queryKey: ['adjudication-stats'] });
      setSelectedItem(null);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.rejectAdjudication(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adjudication-queue'] });
      queryClient.invalidateQueries({ queryKey: ['adjudication-stats'] });
      setSelectedItem(null);
      setRejectReason('');
    },
  });

  const skipMutation = useMutation({
    mutationFn: (id: string) => api.skipAdjudication(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adjudication-queue'] });
      queryClient.invalidateQueries({ queryKey: ['adjudication-stats'] });
      setSelectedItem(null);
    },
  });

  const items: AdjudicationItem[] = queueData?.items || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Match Adjudication</h1>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard
            label="Pending"
            value={stats.pending}
            color="yellow"
            active={statusFilter === 'pending'}
            onClick={() => setStatusFilter('pending')}
          />
          <StatCard
            label="Merged"
            value={stats.approved}
            color="green"
            active={statusFilter === 'approved'}
            onClick={() => setStatusFilter('approved')}
          />
          <StatCard
            label="Rejected"
            value={stats.rejected}
            color="red"
            active={statusFilter === 'rejected'}
            onClick={() => setStatusFilter('rejected')}
          />
          <StatCard
            label="Skipped"
            value={stats.skipped}
            color="gray"
            active={statusFilter === 'skipped'}
            onClick={() => setStatusFilter('skipped')}
          />
          <StatCard
            label="Avg Confidence"
            value={`${stats.avgConfidence}%`}
            color="blue"
            active={false}
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Queue List */}
        <div className="lg:col-span-1 card p-4">
          <h2 className="text-lg font-semibold mb-4">Review Queue</h2>

          <div className="mb-4 space-y-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input w-full"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Merged</option>
              <option value="rejected">Rejected</option>
              <option value="skipped">Skipped</option>
            </select>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : items.length > 0 ? (
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setSelectedItem(item.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedItem === item.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium">
                        {item.entityId1} ↔ {item.entityId2}
                      </span>
                    </div>
                    <StatusBadge status={item.status} />
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <ConfidenceBadge score={item.matchConfidence || 0} />
                    <span className="text-xs text-gray-500">
                      {item.matchType || 'Unknown'}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No items in queue</p>
            </div>
          )}
        </div>

        {/* Detail View */}
        <div className="lg:col-span-2 card p-6">
          {selectedItem && itemDetail ? (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold">Compare Entities</h2>
                <ConfidenceBadge
                  score={itemDetail.item.matchConfidence || 0}
                  large
                />
              </div>

              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Entity 1 */}
                <EntityCard
                  entity={itemDetail.entity1}
                  label="Entity 1"
                  entityId={itemDetail.item.entityId1}
                />

                {/* Entity 2 */}
                <EntityCard
                  entity={itemDetail.entity2}
                  label="Entity 2"
                  entityId={itemDetail.item.entityId2}
                />
              </div>

              {/* Why Analysis Section */}
              {whyData && (
                <div className="mb-6 border rounded-lg overflow-hidden">
                  <div className="bg-blue-50 px-4 py-3 border-b flex items-center gap-2">
                    <HelpCircle className="h-5 w-5 text-blue-600" />
                    <h3 className="font-medium text-blue-900">Why These Entities Might Match</h3>
                    {whyData.matchInfo && (
                      <span className="ml-auto text-sm bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                        {whyData.matchInfo.matchLevel} • Key: {whyData.matchInfo.whyKey}
                      </span>
                    )}
                  </div>

                  {/* Feature Comparison Table */}
                  <div className="p-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500">
                          <th className="pb-2 font-medium">Attribute</th>
                          <th className="pb-2 font-medium">Entity 1</th>
                          <th className="pb-2 font-medium">Entity 2</th>
                          <th className="pb-2 font-medium text-right">Score</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {whyData.featureComparison?.map((feature, idx) => (
                          <tr key={idx}>
                            <td className="py-2 font-medium text-gray-700">{feature.featureType}</td>
                            <td className="py-2 text-gray-900">{feature.entity1Value || '—'}</td>
                            <td className="py-2 text-gray-900">{feature.entity2Value || '—'}</td>
                            <td className="py-2 text-right">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                feature.score >= 95 ? 'bg-green-100 text-green-800' :
                                feature.score >= 80 ? 'bg-yellow-100 text-yellow-800' :
                                feature.score >= 60 ? 'bg-orange-100 text-orange-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {feature.score}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Merge Preview */}
              {itemDetail.item.status === 'pending' && (
                <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <GitMerge className="h-6 w-6 text-amber-600" />
                    <div>
                      <h4 className="font-medium text-amber-900">What happens on Confirm Match?</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        <span className="font-semibold">Entity {itemDetail.item.entityId2}</span>
                        {' '}<ArrowRight className="inline h-4 w-4" />{' '}
                        will merge INTO{' '}
                        <span className="font-semibold">Entity {itemDetail.item.entityId1}</span>
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        All records from Entity 2 will be linked to Entity 1. This creates an audit trail.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              {itemDetail.item.status === 'pending' && (
                <div className="border-t pt-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">
                    Decision
                  </h3>

                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <button
                        onClick={() => approveMutation.mutate(selectedItem)}
                        disabled={approveMutation.isPending}
                        className="flex-1 btn-primary flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="h-5 w-5" />
                        Confirm Match
                      </button>
                      <button
                        onClick={() => skipMutation.mutate(selectedItem)}
                        disabled={skipMutation.isPending}
                        className="btn-secondary flex items-center gap-2"
                      >
                        <SkipForward className="h-5 w-5" />
                        Skip
                      </button>
                    </div>

                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Reason for rejection..."
                        className="input flex-1"
                      />
                      <button
                        onClick={() =>
                          rejectMutation.mutate({
                            id: selectedItem,
                            reason: rejectReason,
                          })
                        }
                        disabled={
                          rejectMutation.isPending || !rejectReason.trim()
                        }
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        <XCircle className="h-5 w-5" />
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Completed decision info */}
              {itemDetail.item.status !== 'pending' && (
                <div className="border-t pt-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      Decision Made
                    </h3>
                    <div className="flex items-center gap-2 text-sm">
                      <StatusBadge status={itemDetail.item.status} />
                      <span className="text-gray-500">
                        by {itemDetail.item.decidedBy || 'Unknown'}
                      </span>
                      {itemDetail.item.decidedAt && (
                        <span className="text-gray-400">
                          on{' '}
                          {new Date(
                            itemDetail.item.decidedAt
                          ).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {itemDetail.item.notes && (
                      <p className="mt-2 text-sm text-gray-600">
                        {itemDetail.item.notes}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <div className="text-center">
                <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Select an item from the queue to review</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
  active,
  onClick,
}: {
  label: string;
  value: number | string;
  color: string;
  active?: boolean;
  onClick?: () => void;
}) {
  const colorClasses: Record<string, string> = {
    yellow: 'bg-yellow-50 border-yellow-200',
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
    gray: 'bg-gray-50 border-gray-200',
    blue: 'bg-blue-50 border-blue-200',
  };

  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`p-4 rounded-lg border text-left transition-all ${
        colorClasses[color]
      } ${active ? 'ring-2 ring-primary-500' : ''} ${
        onClick ? 'hover:opacity-80 cursor-pointer' : ''
      }`}
    >
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { class: string; label: string }> = {
    pending: { class: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
    approved: { class: 'bg-green-100 text-green-800', label: 'Merged' },
    rejected: { class: 'bg-red-100 text-red-800', label: 'Rejected' },
    skipped: { class: 'bg-gray-100 text-gray-800', label: 'Skipped' },
  };

  const { class: className, label } = config[status] || config.pending;

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );
}

function ConfidenceBadge({
  score,
  large,
}: {
  score: number;
  large?: boolean;
}) {
  let className = 'bg-gray-100 text-gray-800';
  if (score >= 95) {
    className = 'bg-green-100 text-green-800';
  } else if (score >= 80) {
    className = 'bg-yellow-100 text-yellow-800';
  } else if (score >= 60) {
    className = 'bg-orange-100 text-orange-800';
  } else {
    className = 'bg-red-100 text-red-800';
  }

  return (
    <span
      className={`inline-flex items-center rounded font-medium ${className} ${
        large ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs'
      }`}
    >
      {score}% Match
    </span>
  );
}

function EntityCard({
  entity,
  label,
  entityId,
}: {
  entity: {
    entityId: number;
    bestName: string;
    bestAddress: string | null;
    bestDOB: string | null;
    recordCount: number;
    dataSources: string[];
    linkedRecords?: Array<{
      dataSource: string;
      recordId: string;
      matchKey: string;
      rawData: Record<string, unknown>;
    }>;
  } | null;
  label: string;
  entityId: number;
}) {
  if (!entity) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-500 mb-2">{label}</h3>
        <p className="text-gray-400">Entity {entityId} not found</p>
      </div>
    );
  }

  // Extract loyalty accounts from linked records
  const loyaltyAccounts = entity.linkedRecords
    ?.filter(r => r.rawData?.LOYALTY_ID)
    .map(r => ({
      loyaltyId: r.rawData.LOYALTY_ID as string,
      dataSource: r.dataSource,
      recordId: r.recordId,
      // IS_DAP_FLAG: '00' = not confirmed, '01' = confirmed DAP
      isDap: r.rawData.IS_DAP_FLAG === '01',
    })) || [];

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-500">{label}</h3>
        <Link
          to={`/entities/${entity.entityId}`}
          className="text-xs text-primary-600 hover:text-primary-700"
        >
          View Details
        </Link>
      </div>

      {/* Entity ID Badge */}
      <div className="mb-2">
        <span className="text-xs text-gray-400">Entity ID:</span>
        <span className="ml-1 font-mono text-sm font-semibold text-gray-700">{entity.entityId}</span>
      </div>

      <h4 className="text-lg font-semibold text-gray-900 mb-2">
        {entity.bestName}
      </h4>
      <dl className="space-y-1 text-sm">
        {entity.bestDOB && (
          <div className="flex justify-between">
            <dt className="text-gray-500">DOB:</dt>
            <dd className="text-gray-900">{entity.bestDOB}</dd>
          </div>
        )}
        {entity.bestAddress && (
          <div className="flex justify-between">
            <dt className="text-gray-500">Location:</dt>
            <dd className="text-gray-900">{entity.bestAddress}</dd>
          </div>
        )}
        <div className="flex justify-between">
          <dt className="text-gray-500">Records:</dt>
          <dd className="text-gray-900">{entity.recordCount}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">Sources:</dt>
          <dd className="text-gray-900">{entity.dataSources.join(', ')}</dd>
        </div>
      </dl>

      {/* Loyalty Accounts Section */}
      {loyaltyAccounts.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <h5 className="text-xs font-medium text-gray-500 mb-2">Loyalty Accounts</h5>
          <div className="space-y-1">
            {loyaltyAccounts.map((acct, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-mono bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                    {acct.loyaltyId}
                  </span>
                  {acct.isDap && (
                    <span className="bg-red-600 text-white px-1.5 py-0.5 rounded text-[10px] font-semibold">
                      DAP
                    </span>
                  )}
                </div>
                <span className="text-gray-400">{acct.dataSource}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
